import React, { createContext, useContext, useMemo } from "react";
import useSWRImmutable from "swr/immutable";
import { useLocation } from "react-router-dom";
import { db } from "../../../firebase/firebase";
import { collection, getDocs } from "firebase/firestore";
import { Device } from "../web_parts/types";
import { message } from "antd";
import { useAuth } from "../../../context/AuthContext";

interface DeviceContextType {
  devices: Device[] | undefined;
  isLoading: boolean;
  isError: boolean;
  refreshDevices: () => void;
}

const DeviceContext = createContext<DeviceContextType | undefined>(undefined);

/**
 * Firestore の Timestamp または number を number に正規化する。
 * Cloud Functions は number で保存するが、クライアントで Timestamp として返る場合がある。
 */
function toNumber(value: unknown): number | undefined {
  if (value == null) return undefined;
  if (typeof value === "number" && !Number.isNaN(value)) return value;
  if (typeof value === "object" && value !== null && "toMillis" in value) {
    return (value as { toMillis: () => number }).toMillis();
  }
  return undefined;
}

/**
 * Firestore devices のドキュメントをフロントの Device 型にマッピングする。
 * データ構造は functions/src/index.ts の DeviceRecord に準拠（Android が registerDevice で保存する形式）。
 * - ドキュメントID = deviceId（端末固有ID）。フロントでは id / serialNumber として扱う。
 * - DeviceRecord に serialNumber はないため、id と deviceId から serialNumber を設定する。
 */
function mapDocToDevice(docSnap: { id: string; data: () => Record<string, unknown> }): Device {
  const data = docSnap.data();
  const id = docSnap.id;
  const familyEmailsRaw = data.familyEmails;
  const familyEmails = Array.isArray(familyEmailsRaw)
    ? familyEmailsRaw
        .filter((v): v is string => typeof v === "string")
        .map((v) => v.trim().toLowerCase())
        .filter((v) => v.length > 0)
    : undefined;
  // Functions の DeviceRecord は document ID = deviceId。serialNumber は保存されないので id を使用。
  const serialNumber =
    (typeof data.serialNumber === "string" && data.serialNumber) ||
    (typeof data.deviceId === "string" && data.deviceId) ||
    id;

  return {
    id,
    serialNumber,
    deviceName: (data.deviceName as string) || undefined,
    manufacturer: (data.manufacturer as string) || undefined,
    model: (data.model as string) || undefined,
    osVersion: (data.osVersion as string) || undefined,
    appVersion: (data.appVersion as string) || undefined,
    versionCode: typeof data.versionCode === "number" ? data.versionCode : undefined,
    versionName: (data.versionName as string) || undefined,
    lastSeenAt: toNumber(data.lastSeenAt) ?? undefined,
    crashCount: typeof data.crashCount === "number" ? data.crashCount : undefined,
    lastCrashAt: toNumber(data.lastCrashAt) ?? undefined,
    lastCrashSummary: (data.lastCrashSummary as string) || undefined,
    lastCrashVersionCode: typeof data.lastCrashVersionCode === "number" ? data.lastCrashVersionCode : undefined,
    networkStatus: (data.networkStatus as string) || undefined,
    lastSyncTime: toNumber(data.lastSyncTime) ?? undefined,
    updateTime: toNumber(data.updatedAt) ?? toNumber(data.updateTime) ?? undefined,
    isDeleted: data.isDeleted === true,
    userId: (data.userId as string) || undefined,
    authProvider: (data.authProvider as string) || undefined,
    userEmail:
      typeof data.userEmail === "string" && data.userEmail.trim().length > 0
        ? data.userEmail.trim().toLowerCase()
        : undefined,
    userDisplayName: (data.userDisplayName as string) ?? undefined,
    userPhotoUrl: (data.userPhotoUrl as string) ?? undefined,
    managerEmail:
      typeof data.managerEmail === "string" && data.managerEmail.trim().length > 0
        ? data.managerEmail.trim().toLowerCase()
        : undefined,
    familyEmails,
    userInfo: (data.userInfo as Device["userInfo"]) ?? (data.userId != null || data.userDisplayName != null
      ? { userID: String(data.userId ?? ""), userName: String(data.userDisplayName ?? "") }
      : undefined),
    fcmToken: (data.fcmToken as string) || undefined,
    plan: (data.plan as Device["plan"]) ?? undefined,
    expiry: toNumber(data.expiry) ?? undefined,
    conversationTimeRemaining: typeof data.conversationTimeRemaining === "number" ? data.conversationTimeRemaining : undefined,
    totalChatTimeMs: typeof data.totalChatTimeMs === "number" ? data.totalChatTimeMs : undefined,
    totalChatTimeUpdatedAt: toNumber(data.totalChatTimeUpdatedAt) ?? undefined,
    chat_warn: typeof data.chat_warn === "number" ? data.chat_warn : undefined,
    chat_limit: typeof data.chat_limit === "number" ? data.chat_limit : undefined,
  };
}

const fetchDevices = async (): Promise<Device[]> => {
  try {
    const querySnapshot = await getDocs(collection(db, "devices"));
    const list: Device[] = [];
    querySnapshot.docs.forEach((docSnap) => {
      const device = mapDocToDevice({ id: docSnap.id, data: () => docSnap.data() });
      if (!device.isDeleted) {
        list.push(device);
      }
    });
    return list;
  } catch (error) {
    console.error("デバイスデータの取得中にエラー:", error);
    message.error("デバイスデータの取得に失敗しました。");
    throw error;
  }
};

const isDevicesExemptPath = (path: string): boolean => {
  const normalized = path.replace(/^#/, "") || "/";
  return (
    normalized === "/login" ||
    normalized === "/unauthorized" ||
    normalized === "/album-import"
  );
};

export const DeviceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const { currentUser, loading: authLoading } = useAuth();
  // HashRouter では pathname、念のため hash も見る
  const path = (location.pathname || "/").replace(/^#/, "") || "/";
  const hashPath = (location.hash || "").replace(/^#/, "") || "";
  const onExemptPath = isDevicesExemptPath(path) || (hashPath ? isDevicesExemptPath(hashPath) : false);

  // 未ログイン／認証確認中／公開ページでは取得しない（権限エラーとトースト連発を防ぐ）
  const skipDevicesFetch = authLoading || !currentUser || onExemptPath;

  const { data, error, mutate } = useSWRImmutable(
    skipDevicesFetch || !currentUser ? null : `devices:${currentUser.uid}`,
    fetchDevices,
    { shouldRetryOnError: false }
  );

  const value = useMemo<DeviceContextType>(() => {
    if (skipDevicesFetch) {
      return {
        devices: [],
        isLoading: authLoading,
        isError: false,
        refreshDevices: () => undefined,
      };
    }
    return {
      devices: data,
      isLoading: !error && !data,
      isError: !!error,
      refreshDevices: () => {
        void mutate();
      },
    };
  }, [skipDevicesFetch, authLoading, data, error, mutate]);

  return <DeviceContext.Provider value={value}>{children}</DeviceContext.Provider>;
};

export const useDevices = (): DeviceContextType => {
  const context = useContext(DeviceContext);
  if (!context) {
    throw new Error("useDevices は DeviceProvider 内で使用する必要があります");
  }
  return context;
};
